/**
 * 9 周年庆活动 — 接口定义
 * 详见《PTE九周年活动-前端接口文档》：活动聚合、签到会员 / 节日礼 / 小课领取、学习时长上报
 * 约定：服务端空值字段会省略；数组无数据返回 []；领取接口幂等，成功后统一刷新 overview
 */
import request from '@/utils/http'

/** 活动状态 */
export type AnniversaryActivityStatus = 'NOT_STARTED' | 'ACTIVE' | 'ENDED'

/** 可领取奖励状态（每日会员 / 小课 / 节日礼共用） */
export type AnniversaryRewardStatus = 'LOCKED' | 'CLAIMABLE' | 'CLAIMED' | 'EXPIRED'

/** 开奖状态 */
export type AnniversaryLotteryStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED'

/** 奖品类型 */
export type AnniversaryPrizeType = 'RANK_OFFICIAL' | 'DRAW_OFFICIAL' | 'MONTH_CARD' | 'WEEK_CARD'

/** 奖券来源 */
export type AnniversaryTicketSource = 'SIGN_6' | 'SIGN_12' | 'FESTIVAL'

export interface AnniversaryDailyReward {
  /** 签到序号 1-14，领取接口传此值 */
  signDayNo: number
  /** 对应免费会员天数 */
  memberDays: number
  status: AnniversaryRewardStatus
}

export interface AnniversaryCourse {
  id: number
  title: string
  cover: string
}

export interface AnniversaryMilestone {
  /** 3 / 6 / 9 / 12 */
  milestoneDay: number
  rewardType: 'COURSE' | 'TICKET'
  /** 小课与奖券里程碑统一使用领取状态（6/12 天奖券达标后需手动领取） */
  status: AnniversaryRewardStatus
  /** 小课里程碑的可选课程 ID：3 天为 [35,29,10]、9 天为 [9,1]；奖券里程碑为空数组 */
  courseIds?: number[]
  /** 小课选择卡片：按配置顺序返回 id/title/cover；奖券里程碑为空数组 */
  courses?: AnniversaryCourse[]
  /** 已领取小课的课程 ID；未领取或奖券里程碑时字段省略 */
  selectedCourseId?: number
}

export interface AnniversaryFestival {
  /** 是否在 9/25、9/30、10/1 任一天以有效登录态访问 */
  qualified: boolean
  status: AnniversaryRewardStatus
}

export interface AnniversaryTicket {
  /** 全局唯一的 7 位大写券码，可直接展示 */
  ticketCode: string
  sourceType: AnniversaryTicketSource
  /** 0 待开奖；1 本券中奖；2 已用其他券中奖本券退出后续轮次 */
  status: 0 | 1 | 2
  /** 本券中奖时的奖品类型；未中奖时字段省略 */
  prizeType?: AnniversaryPrizeType
}

export interface AnniversaryRankUser {
  rankNo: number
  uid: string
  /** 昵称；资料缺失时字段可能省略 */
  nickName?: string
  /** 头像地址；资料缺失时字段可能省略 */
  icon?: string
  signDays: number
  activeSeconds: number
}

export interface AnniversaryWin {
  /** RANK 冲榜奖励；DRAW 抽奖奖励 */
  awardSource: 'RANK' | 'DRAW'
  prizeType: AnniversaryPrizeType
  /** 抽奖中奖券码；冲榜奖励没有券码，字段省略 */
  ticketCode?: string
  /** 冲榜名次；抽奖中奖时字段省略 */
  rankNo?: number
  /** 0 未兑换；1 已兑换 */
  redeemStatus: 0 | 1
  /** 兑换时间毫秒时间戳；未兑换时字段省略 */
  redeemTime?: number
}

/** 活动聚合数据（弹窗内所有模块的数据源） */
export interface AnniversaryOverview {
  activityCode: string
  activityStatus: AnniversaryActivityStatus
  /** 服务端毫秒时间戳，页面状态和倒计时以此为准 */
  serverTime: number
  startTime: number
  endTime: number
  finalRankTime: number
  /** 签到会员、节日礼和小课补领截止时间 */
  claimDeadline: number
  /** 小课独立补领截止时间（后端已延长至 2026-10-18 23:59:59，与公共 claimDeadline 独立；仅按 milestones[].status 控制领取时无需读取） */
  courseClaimDeadline?: number
  lotteryTime: number
  /** 开奖结果公示时间（活动结束后的“距离开奖还有”倒计时目标） */
  resultPublicTime: number
  /** 开奖结果是否已公示（true 时头部展示“已开奖”） */
  resultPublic: boolean
  maxDailyActiveSeconds: number
  courseIds: number[]
  activityActiveSeconds: number
  signDays: number
  /** 当前展示榜单的快照时间；尚无成功榜单时字段省略 */
  rankSnapshotTime?: number
  lotteryStatus: AnniversaryLotteryStatus
  dailyRewards: AnniversaryDailyReward[]
  milestones: AnniversaryMilestone[]
  festival: AnniversaryFestival
  tickets: AnniversaryTicket[]
  topRanks: AnniversaryRankUser[]
  /** 当前用户在同一榜单中的名次；匿名、未上榜或无榜单时字段省略 */
  myRank?: AnniversaryRankUser
  wins: AnniversaryWin[]
}

/** 领取签到会员奖励返回（后端一次领取当前全部符合条件的节点） */
export interface AnniversarySignClaimResult {
  /** 本次新领取的签到节点数量 */
  claimedCount: number
  /** 本次累计发放的免费会员天数 */
  grantedMemberDays: number
  /** 本次新领取的签到节点，按节点升序返回 */
  claimedSignDayNos: number[]
}

/** 领取接口成功返回的奖励记录 */
export interface AnniversaryClaimResult {
  id: number
  uid: string
  rewardType: 'DAILY_MEMBER' | 'FESTIVAL' | 'COURSE' | 'TICKET'
  rewardKey: string
  /** 实际发放的奖励值（会员天数 / 课程有效天数） */
  rewardValue: number
  courseId?: number
  /** 奖券领取成功返回：全局唯一券码与来源 */
  ticketCode?: string
  sourceType?: AnniversaryTicketSource
  status: number
  claimedAt: number
}

/** overview 空数据兜底（请求失败时保证各模块可安全渲染） */
export const emptyAnniversaryOverview = (): AnniversaryOverview => ({
  activityCode: 'PTE_9TH_2026',
  activityStatus: 'NOT_STARTED',
  serverTime: 0,
  startTime: 0,
  endTime: 0,
  finalRankTime: 0,
  claimDeadline: 0,
  lotteryTime: 0,
  resultPublicTime: 0,
  resultPublic: false,
  maxDailyActiveSeconds: 43200,
  courseIds: [],
  activityActiveSeconds: 0,
  signDays: 0,
  lotteryStatus: 'PENDING',
  dailyRewards: [],
  milestones: [],
  festival: { qualified: false, status: 'LOCKED' },
  tickets: [],
  topRanks: [],
  wins: []
})

/**
 * 活动聚合数据（进入弹窗 / 领取成功后刷新）
 * 匿名可请求；登录后首次请求会先自动签到再返回，前端不要自行增加签到天数
 */
export const getAnniversaryOverviewApi = () => {
  return request.get<AnniversaryOverview>('/api/anniversary/overview', undefined, { skipMsg: true })
}

/**
 * 领取某一天签到会员奖励
 * @param signDayNo 签到序号 1-14，对应 dailyRewards[].signDayNo
 */
export const claimSignRewardApi = (signDayNo: number) => {
  return request.post<AnniversarySignClaimResult>(
    '/api/anniversary/sign/reward/claim',
    { signDayNo },
    { skipMsg: true }
  )
}

/** 领取节日登录礼（无需请求体；一次性发放 7 天会员 + 1 张节日奖券，重复点击不重复发放） */
export const claimFestivalRewardApi = () => {
  return request.post<AnniversaryClaimResult>('/api/anniversary/festival/reward/claim', undefined, {
    skipMsg: true
  })
}

/**
 * 领取 3 天 / 9 天签到里程碑小课
 * @param milestoneDay 仅支持 3 或 9
 * @param courseId 必须为该节点 courses[].id，不可跨节点提交
 */
export const claimCourseRewardApi = (milestoneDay: number, courseId: number) => {
  return request.post<AnniversaryClaimResult>(
    '/api/anniversary/course/reward/claim',
    { milestoneDay, courseId },
    { skipMsg: true }
  )
}

/**
 * 领取 6 天 / 12 天签到里程碑奖券（幂等，重复请求返回原券码）
 * @param milestoneDay 仅支持 6 或 12
 */
export const claimTicketRewardApi = (milestoneDay: number) => {
  return request.post<AnniversaryClaimResult>(
    '/api/anniversary/ticket/reward/claim',
    { milestoneDay },
    { skipMsg: true }
  )
}

export interface AnniversaryActiveTimeReportPayload {
  /** 客户端当日稳定标识（同一端 / 设备 / 自然日保持不变，按北京时间跨天切换） */
  clientDayId: string
  /** 该 clientDayId 当天累计有效学习秒数（传累计值，只增不减） */
  clientAccumulatedSeconds: number
}

export interface AnniversaryActiveTimeReportVo {
  /** 本次实际计入的增量秒数；重复 / 乱序请求为 0 */
  acceptedSeconds: number
  /** 当前用户当天 App + Web 合计有效秒数 */
  dailySeconds: number
  /** 当前用户整个活动期累计有效秒数 */
  activitySeconds: number
  /** 本次原始增量是否因每日 12 小时上限被截断 */
  truncated: boolean
  /** 是否还需要继续周期上报；达到 43200 秒后为 false */
  continueReport: boolean
}

/** 活动学习时长上报（全局后台调用；与新人福利 /api/study/active-time/report 不混用） */
export const reportAnniversaryActiveTimeApi = (payload: AnniversaryActiveTimeReportPayload) => {
  return request.post<AnniversaryActiveTimeReportVo>(
    '/api/anniversary/active-time/report',
    payload,
    { skipMsg: true }
  )
}
